/**
 * Lecture de l'inventaire distant (`json/09_remote_inventory.json`) — module 09
 * « collecteur central » du contrat toolkit 1.2.
 *
 * Une seule exécution, sur une machine du domaine et avec un compte de service
 * en lecture seule, interroge l'annuaire, énumère les partages et leurs droits,
 * relève le matériel des serveurs et inventorie les fichiers à distance (SSH ou
 * partage administratif). Ce qu'elle n'a PAS pu couvrir devient un **plan de
 * déploiement** : la liste des machines où une sonde locale reste nécessaire,
 * avec la commande à lancer.
 *
 * Trois règles du contrat sont appliquées ici, à la lettre :
 *
 * 1. `null` n'est pas `false` et n'est pas `0`. Une machine jamais contactée a
 *    `reachable: null` et s'affiche « non testé », jamais « injoignable ».
 * 2. Chaque fait porte sa provenance : la machine OBSERVÉE, celle qui a
 *    collecté, la méthode, la preuve et la confiance.
 * 3. Une lacune est un fait : `coverage.hosts_skipped[]` et `deployment_plan[]`
 *    sont conservés tels quels, jamais filtrés — c'est la valeur du livrable.
 *
 * Le module est écrit en lecture tolérante : un zip qui ne porte pas le module
 * 09, ou un stub d'erreur (`module_error.schema.json`), donne `null` et
 * l'onglet « Parc » n'est pas proposé.
 */

type Json = Record<string, unknown>;

const isObj = (v: unknown): v is Json => !!v && typeof v === 'object' && !Array.isArray(v);
const arr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const objs = (v: unknown): Json[] => arr(v).filter(isObj);
const obj = (v: unknown): Json => (isObj(v) ? v : {});
const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const bool = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null);
const strs = (v: unknown): string[] => arr(v).filter((x): x is string => typeof x === 'string' && x !== '');

/** Provenance commune à toute donnée du contrat : qui a vu quoi, comment, et à quel point c'est sûr. */
interface FleetProvenance {
  host: string | null;
  collectedBy: string | null;
  method: string | null;
  evidence: string | null;
  confidence: number | null;
}

interface FleetInventory {
  method: string | null;
  fileCount: number | null;
  sizeBytes: number | null;
  /** `null` = l'inventaire n'a pas tourné ; `false` = il est allé au bout. */
  truncated: boolean | null;
  elapsedS: number | null;
}

interface FleetHost {
  name: string;
  fqdn: string | null;
  os: string | null;
  roles: string[];
  /** D'où vient la machine : `active_directory`, `dns`, `scan`, `manual`… */
  source: string | null;
  /** `null` = jamais contactée (« non testé »), pas « injoignable ». */
  reachable: boolean | null;
  /** Sondes ouvertes (`smb`, `ssh`, `winrm`…), telles que déclarées par `probe{}`. */
  probes: string[];
  /** `null` quand aucun inventaire fichier n'a été tenté sur cette machine. */
  inventory: FleetInventory | null;
  provenance: FleetProvenance | null;
}

interface FleetAce { principal: string; rights: string[] }

interface FleetShare {
  host: string | null;
  name: string;
  path: string | null;
  protocol: string | null;
  acl: FleetAce[];
}

interface FleetSystem {
  host: string | null;
  cpu: string | null;
  ram: number | null;
  disks: number | null;
  os: string | null;
  method: string | null;
}

interface FleetFileRef {
  host: string | null;
  share: string | null;
  /** Membre NDJSON du zip (`json/09_files_<hash8>.ndjson`). */
  member: string | null;
  fileCount: number | null;
  sizeBytes: number | null;
  method: string | null;
  truncated: boolean | null;
}

/** Une machine vue mais non inventoriée : la raison ET la piste pour y remédier. */
interface FleetSkipped { host: string; reason: string | null; hint: string | null }

interface FleetCoverage {
  hostsTotal: number | null;
  hostsProbed: number | null;
  hostsInventoried: number | null;
  skipped: FleetSkipped[];
  methodsUsed: string[];
  /** `null` = la clé n'existe pas ; `false` = le budget n'a pas été atteint. */
  budgetExhausted: boolean | null;
}

/** Une ligne du plan de déploiement : la machine, pourquoi, et la commande à copier. */
interface FleetAction { host: string; reason: string | null; hint: string | null; command: string | null }

export interface RemoteInventory {
  directory: { type: string | null; domain: string | null; controllers: string[] };
  hosts: FleetHost[];
  shares: FleetShare[];
  systems: FleetSystem[];
  fileRefs: FleetFileRef[];
  coverage: FleetCoverage;
  plan: FleetAction[];
}

function parseProvenance(v: unknown): FleetProvenance | null {
  if (!isObj(v)) return null;
  return {
    host: str(v.host),
    collectedBy: str(v.collected_by),
    method: str(v.method),
    evidence: str(v.evidence),
    confidence: num(v.confidence),
  };
}

/** `probe{smb: true, ssh: false, winrm: null}` → `['smb']`. Un `probe` vide donne `[]`. */
function parseProbes(v: unknown): string[] {
  return Object.entries(obj(v))
    .filter(([, open]) => open === true)
    .map(([name]) => name)
    .sort();
}

function parseInventory(v: unknown): FleetInventory | null {
  if (!isObj(v) || Object.keys(v).length === 0) return null;
  return {
    method: str(v.method),
    fileCount: num(v.file_count),
    sizeBytes: num(v.size_bytes),
    truncated: bool(v.truncated),
    elapsedS: num(v.elapsed_s),
  };
}

function parseHost(h: Json): FleetHost {
  return {
    name: str(h.name) ?? str(h.fqdn) ?? '—',
    fqdn: str(h.fqdn),
    os: str(h.os),
    roles: strs(h.roles),
    source: str(h.source),
    reachable: bool(h.reachable),
    probes: parseProbes(h.probe),
    inventory: parseInventory(h.inventory),
    provenance: parseProvenance(h.provenance),
  };
}

/** Une ACE est soit `{principal, rights[]}`, soit une chaîne (`"DOMAIN\\Users:R"`). */
function parseAcl(v: unknown): FleetAce[] {
  return arr(v).flatMap(entry => {
    if (typeof entry === 'string' && entry !== '') return [{ principal: entry, rights: [] }];
    if (!isObj(entry)) return [];
    const principal = str(entry.principal) ?? str(entry.name) ?? str(entry.trustee);
    return principal ? [{ principal, rights: strs(entry.rights) }] : [];
  });
}

function parseCoverage(v: unknown): FleetCoverage {
  const c = obj(v);
  return {
    hostsTotal: num(c.hosts_total),
    hostsProbed: num(c.hosts_probed),
    hostsInventoried: num(c.hosts_inventoried),
    skipped: objs(c.hosts_skipped).flatMap(s => {
      const host = str(s.host);
      return host ? [{ host, reason: str(s.reason), hint: str(s.hint) }] : [];
    }),
    methodsUsed: [...new Set(strs(c.methods_used))],
    budgetExhausted: bool(c.budget_exhausted),
  };
}

/**
 * `json/09_remote_inventory.json` → structure prête à afficher, ou `null` quand
 * l'audit ne porte pas de collecte distante (contrat 1.0 / 1.1, module 09 non
 * lancé, ou stub d'erreur — qui n'a aucune des clés du contrat).
 */
export function parseRemoteInventory(json: unknown): RemoteInventory | null {
  if (!isObj(json)) return null;
  const known = ['directory', 'hosts', 'shares', 'remote_systems', 'file_inventory_refs',
    'coverage', 'deployment_plan'];
  if (!known.some(k => json[k] !== undefined)) return null;
  const directory = obj(json.directory);
  return {
    directory: {
      type: str(directory.type),
      domain: str(directory.domain),
      controllers: strs(directory.controllers),
    },
    hosts: objs(json.hosts).map(parseHost),
    shares: objs(json.shares).map(s => ({
      host: str(s.host),
      name: str(s.name) ?? str(s.path) ?? '—',
      path: str(s.path),
      protocol: str(s.protocol),
      acl: parseAcl(s.acl),
    })),
    systems: objs(json.remote_systems).map(r => ({
      host: str(r.host),
      cpu: str(r.cpu),
      ram: num(r.ram),
      disks: num(r.disks),
      os: str(r.os),
      method: str(r.method),
    })),
    fileRefs: objs(json.file_inventory_refs).map(f => ({
      host: str(f.host),
      share: str(f.share),
      member: str(f.ndjson_member),
      fileCount: num(f.file_count),
      sizeBytes: num(f.size_bytes),
      method: str(f.method),
      truncated: bool(f.truncated),
    })),
    coverage: parseCoverage(json.coverage),
    plan: objs(json.deployment_plan).flatMap(a => {
      const host = str(a.host);
      return host ? [{ host, reason: str(a.reason), hint: str(a.hint), command: str(a.command) }] : [];
    }),
  };
}

/**
 * Toutes les machines nommées par la collecte, sans doublon : le nom court et
 * le FQDN de chaque hôte, les hôtes cités par les partages, le matériel distant
 * et les renvois d'inventaire, plus les machines ignorées et celles du plan de
 * déploiement. C'est la colonne « observé » du rapprochement questionnaire.
 */
export function fleetHostNames(fleet: RemoteInventory | null): string[] {
  if (!fleet) return [];
  const names = [
    ...fleet.hosts.flatMap(h => [h.name, h.fqdn]),
    ...fleet.shares.map(s => s.host),
    ...fleet.systems.map(s => s.host),
    ...fleet.fileRefs.map(f => f.host),
    ...fleet.coverage.skipped.map(s => s.host),
    ...fleet.plan.map(a => a.host),
  ];
  return [...new Set(names.filter((n): n is string => !!n && n !== '—'))];
}

/** Nombre total de fichiers inventoriés à distance ; un membre NDJSON n'est compté qu'une fois. */
export function remoteFileTotals(fleet: RemoteInventory | null): { files: number; bytes: number; truncated: boolean } {
  const seen = new Set<string>();
  let files = 0, bytes = 0, truncated = false;
  for (const ref of fleet?.fileRefs ?? []) {
    if (ref.member) {
      if (seen.has(ref.member)) continue;
      seen.add(ref.member);
    }
    files += ref.fileCount ?? 0;
    bytes += ref.sizeBytes ?? 0;
    truncated = truncated || ref.truncated === true;
  }
  return { files, bytes, truncated };
}
